/* Theme Composable - Dark/Light Mode Management */
import { ref, watch, onMounted } from 'vue'

const THEME_KEY = 'jardinera-theme'
const THEME_ATTR = 'data-theme'

const theme = ref<'light' | 'dark'>('dark')
const systemTheme = ref<'light' | 'dark'>('dark')

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'dark'
}

function applyTheme(newTheme: 'light' | 'dark') {
  theme.value = newTheme
  document.documentElement.setAttribute(THEME_ATTR, newTheme)
  localStorage.setItem(THEME_KEY, newTheme)
}

function initTheme() {
  // Get stored theme or system preference
  const stored = localStorage.getItem(THEME_KEY) as 'light' | 'dark' | null
  systemTheme.value = getSystemTheme()
  
  if (stored) {
    applyTheme(stored)
  } else {
    applyTheme(systemTheme.value)
  }
  
  // Listen for system theme changes
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      systemTheme.value = e.matches ? 'dark' : 'light'
      // Only auto-switch if user hasn't set a preference
      if (!localStorage.getItem(THEME_KEY)) {
        applyTheme(systemTheme.value)
      }
    })
  }
}

function toggleTheme() {
  applyTheme(theme.value === 'dark' ? 'light' : 'dark')
}

function setTheme(newTheme: 'light' | 'dark') {
  applyTheme(newTheme)
}

export function useTheme() {
  onMounted(initTheme)
  
  return {
    theme,
    systemTheme,
    toggleTheme,
    setTheme,
    initTheme,
    isDark: () => theme.value === 'dark',
    isLight: () => theme.value === 'light',
  }
}